<template lang="pug">
    include ../../icon/arrow

    div(ref="_in",
        class="_ptime _con",
        :class="_class",
        :style="{ width, height }",
        :id="id",
        :inert="inert || disabled",
        :tabindex="inert || disabled || anchor ? null : tabindex",
        v-on="__on")
        div(class="_ptimei numeric")
            div(class="_ptimec")
                ib-button(class="link", @click="decHour")
                    +icon-arrow-up4
                div(class="_ptimep")
                    div(v-for="(h,i) in hours", :aria-selected="i == hi") {{ h }}
                    div(v-if="hi < 0", :aria-selected="true", class="fg-error") {{ ha }}
                ib-button(class="link", @click="incHour")
                    +icon-arrow-down4
            div(class="_ptimes") :
            div(class="_ptimec")
                ib-button(class="link", @click="decMinute")
                    +icon-arrow-up4
                div(class="_ptimep")
                    div(v-for="(m,i) in minutes", :aria-selected="i == mi") {{ m }}
                    div(v-if="mi < 0", :aria-selected="true", class="fg-error") {{ ma }}
                ib-button(class="link", @click="incMinute")
                    +icon-arrow-down4
            template(v-if="$locale.date.hour12")
                div
                div(class="_ptimec")
                    ib-button(class="link", :disabled="pi == 0", @click="decPeriod")
                        +icon-arrow-up4
                    div(class="_ptimep")
                        div(v-for="(p,i) in $locale.date.period", :aria-selected="i == pi") {{ p }}
                    ib-button(class="link", :disabled="pi == 1", @click="incPeriod")
                        +icon-arrow-down4
</template>

<script>
    import entry from './ib-form-entry.vue';
    import telemetry from '../private/telemetry';
    import $date from '../../prototype/date';

    export default {
        name: 'ib-picker-time',

        mixins: [
            entry,
            telemetry
        ],

        props: {
            /**
             * The selected time in HH:MM format; generally use v-model
             */
            value: {
                type: String,
                default: undefined
            },
            /**
             * The minimum time in HH:MM format
             */
            min: {
                type: String,
                default: '00:00'
            },
            /**
             * The maximum time in HH:MM format
             */
            max: {
                type: String,
                default: '23:59'
            },
            /**
             * Stepping interval in minutes.
             */
            step: {
                type: Number,
                default: 1
            },
            /**
             * Appearance modifier: `frameless` is supported.
             */
            appearance: String
        },

        data() {
            return {
                ha: '',
                ma: '',
                hi: -1,
                mi: -1,
                pi: -1,
                validity: null
            };
        },

        computed: {
            _class() {
                return {
                    _fo: this.focused,
                    _da: this.disabled,
                    ['_e' + this.appearance]: this.appearance
                };
            },

            hours() {
                const h12 = this.$locale.date.hour12;
                const fmt = new Intl.NumberFormat(this.$locale.current, { numberingSystem: 'latn', minimumIntegerDigits: h12 ? 1 : 2 });
                const r = new Array(h12 ? 12 : 24);
                for (let i = 0; i < r.length; i++) {
                    r[i] = fmt.format(h12 ? (i || 12) : i);
                }
                return r;
            },

            minutes() {
                const step = this.step;
                if (60 % step || !(step > 0)) {
                    throw new RangeError('step out of range');
                }
                const fmt = new Intl.NumberFormat(this.$locale.current, { numberingSystem: 'latn', minimumIntegerDigits: 2 });
                const r = new Array(60 / step);
                for (let i = 0; i < r.length; i++) {
                    r[i] = fmt.format(i * step);
                }
                return r;
            },

            minPart() {
                const min = this.min.split(':');
                if (min.length != 2 || !(min[0] >= 0 && min[0] < 24) || !(min[1] >= 0 && min[1] < 60) || min[0] % 1 || min[1] % 1) {
                    throw new RangeError('min out of range');
                }
                return [+min[0], +min[1]];
            }
        },

        methods: {

            /** @private */
            _sync() {
                let ivalue = this.ivalue;
                let ha, ma;
                if (ivalue == null) {
                    ha = 0;
                    ma = 0;
                } else {
                    [ha,ma] = ivalue.split(':');
                }
                let hi = +ha;
                let mi = ma / this.step;
                this.ha = ha;
                this.ma = ma;
                this.hi = hi >= 0 && hi <= 23 && hi % 1 == 0 ? hi : -1;
                this.mi = mi >= 0 && mi <= 59 && mi % 1 == 0 ? mi : -1;
                this.pi = this.$locale.date.hour12 ? +(hi >= 12) : 0;
            },

            decHour() {
                if (!this.hi--) {
                    this.hi = this.hours.length - 1;
                }
                this.change();
            },

            incHour() {
                if (++this.hi >= this.hours.length) {
                    this.hi = 0;
                }
                this.change();
            },

            decMinute() {
                if (--this.mi < 0) {
                    this.mi = this.minutes.length - 1;
                }
                this.change();
            },

            incMinute() {
                if (++this.mi >= this.minutes.length) {
                    this.mi = 0;
                }
                this.change();
            },

            decPeriod() {
                if (this.pi) {
                    this.pi = 0;
                    this.change();
                }
            },

            incPeriod() {
                if (!this.pi) {
                    this.pi = 1;
                    this.change();
                }
            },

            change() {
                const h = this.hi + this.pi * 12;
                const m = this.mi * this.step;
                this._set((h < 10 ? '0' + h : h) + ':' + (m < 10 ? '0' + m : m));
            },

            validate() {
                return true;
            }
        }
    };
</script>

<style lang="less">
    @import "../../less/globals.less";

    /** Outer container */
    ._ptime {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        position: relative;
        background-color: @bg-field;
        .font-size(@fs3);
        font-weight: 500;
        overflow: hidden;
        height: 5em;

        &._da {
            opacity: .5;
        }
    }

    /** Inner container */
    ._ptimei {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        height: 100%;
        max-width: 7em;
    }

    /** Column containing value and up/down buttons */
    ._ptimec {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-around;
        height: 100%;
    }

    /** Separator (vertically centered colon) */
    ._ptimes {
        margin-top: -.125em;
    }

    /** Picker */
    ._ptimep {
        text-align: center;
        > div {
            visibility: hidden;
            height: 0;
        }
        > [aria-selected=true] {
            visibility: visible;
            height: auto;
        }
    }
</style>

<docs>
    Examples:
    ```jsx
        new Vue({
            data() {
                return {
                    value: null
                }
            },
            template:
                `<ib-context>
                    <p class="after-md">Basic Example: {{value}}</p>
                    <ib-pick-date v-model="value"/>
                    <p class="before-lg after-md">Panning: {{value}}</p>
                    <ib-pick-date v-model="value" pan/>
                    <p class="before-lg after-md">Compact: {{value}}</p>
                    <ib-pick-date v-model="value" class="compact"/>
                    <p class="before-lg after-md">Compact, Disabled: {{value}}</p>
                    <ib-pick-date v-model="value" class="compact" disabled/>
                </ib-context>`
        })
    ```
</docs>
